/**
 * Tab drag & drop plumbing, shared by the tab strip and the pane drop zones.
 *
 * The payload rides on a private MIME type, so nothing else on the page (file
 * uploads, text selections) can be mistaken for a tab, and a tab can never be
 * dropped into an upload zone.
 */
export const TAB_DND_TYPE = "application/x-aron-tab";

export interface TabDragPayload {
  groupId: string;
  key: string;
}

export function readTabDrag(e: React.DragEvent): TabDragPayload | null {
  try {
    const raw = e.dataTransfer.getData(TAB_DND_TYPE);
    const p = raw ? JSON.parse(raw) : null;
    return p && typeof p.groupId === "string" && typeof p.key === "string" ? p : null;
  } catch {
    return null;
  }
}

/** During dragover the payload is unreadable — only its type is. */
export const hasTabDrag = (e: React.DragEvent) =>
  Array.from(e.dataTransfer.types).includes(TAB_DND_TYPE);

/** Set by every drop zone; read once by dragend to tell "landed somewhere" from
 * "let go outside the app". Module-level because the two events belong to
 * different components (the source tab vs. the target strip/pane). */
let tabDropHandled = false;
export const markTabDropHandled = () => {
  tabDropHandled = true;
};
export const beginTabDrag = () => {
  tabDropHandled = false;
};
export const wasTabDropHandled = () => tabDropHandled;

/** Slack (px) before a release outside the window counts as "torn off" — the
 * edge itself is where a clumsy drop lands, not where an intentional one does. */
const TEAR_OFF_MARGIN = 12;

/**
 * Was the tab let go OUTSIDE this browser window?
 *
 * The web cannot tear an element off into an OS window the way VS Code does, so
 * this is the closest honest equivalent: drag the tab past the edge of the
 * window, let go, and we open it as a separate window ourselves. Screen
 * coordinates are the only ones still meaningful once the pointer has left the
 * page. Any drop the app itself handled (a strip, a pane) never reaches here.
 */
export function droppedOutsideWindow(e: React.DragEvent): boolean {
  const { screenX: sx, screenY: sy } = e;
  if (!sx && !sy) return false; // no usable coordinates — assume "inside"
  const left = window.screenX;
  const top = window.screenY;
  return (
    sx < left - TEAR_OFF_MARGIN ||
    sy < top - TEAR_OFF_MARGIN ||
    sx > left + window.outerWidth + TEAR_OFF_MARGIN ||
    sy > top + window.outerHeight + TEAR_OFF_MARGIN
  );
}
